import { Alert, Button, Col, Form, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import DynamicForm from "../controls/DynamicForm";
import InModal from "../controls/InModal";
import { IncidentTypeNotifier } from "../repository/data.default";
import { QueryOperation } from "../code/search-query/search-query";
import {
  addCompanyIncidentNotifier,
  deleteCompanyIncidentNotifier,
  getCompanyIncidentNotifiers,
  getCompanyUserRoles,
  getIncidentTypes,
  updateCompanyIncidentNotifier,
} from "../repository/data.repository";
import { getProblemMessage } from "../utils/http";
import { IncidentNotifierForm } from "./IncidentNotifierView.default";

export interface IncidentNotifierViewProps {
  readonly uid?: string | null;
  readonly companyUid: string;
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly onFinish?: () => void;
  readonly onClose?: () => void;
}

function IncidentNotifierView(props: IncidentNotifierViewProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<IncidentNotifierForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "Something went wrong, try again",
  );
  const [item, setItem] = useState<IncidentTypeNotifier | null>(null);

  // Incident types are a platform-level (unscoped) reference catalog.
  const incidentTypesRequest = async (search: string) => {
    const { data } = await getIncidentTypes({
      searchQuery: {
        conditions: [
          {
            field: "name",
            operation: QueryOperation.StartsWith,
            values: [search],
          },
        ],
      },
      pageNumber: 0,
      pageSize: 100,
    });
    return data.map((t) => ({ display: t.name, value: t.uid }));
  };

  // Company roles are scoped to this company.
  const companyRolesRequest = async (search: string) => {
    const { data } = await getCompanyUserRoles({
      searchQuery: {
        conditions: [
          {
            field: "companyUid",
            operation: QueryOperation.Equal,
            values: [props.companyUid],
          },
          {
            field: "name",
            operation: QueryOperation.StartsWith,
            values: [search],
          },
        ],
      },
      pageNumber: 0,
      pageSize: 100,
    });
    return data.map((r) => ({ display: r.name, value: r.uid }));
  };

  useEffect(() => {
    if (props.open) loadData();
    else reset();
  }, [props.open, props.uid]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((v) => setValid(!!Object.values(v).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  function loadData(): void {
    if ((props.mode !== "update" && props.mode !== "delete") || !props.uid) {
      reset();
      return;
    }
    setLoading(true);
    setError(false);
    // No dedicated get-by-id endpoint — fetch the single notifier by uid from the scoped list.
    getCompanyIncidentNotifiers({
      searchQuery: {
        conditions: [
          {
            field: "companyUid",
            operation: QueryOperation.Equal,
            values: [props.companyUid],
          },
          { field: "uid", operation: QueryOperation.Equal, values: [props.uid] },
        ],
      },
      pageNumber: 0,
      pageSize: 1,
    })
      .then(({ data }) => {
        setLoading(false);
        const n = data[0];
        setItem(n ?? null);
        if (n) {
          form.setFieldsValue({
            incidentType: { display: n.incidentTypeName, value: n.incidentTypeUid },
            companyRole: { display: n.companyRoleName, value: n.companyRoleUid },
          });
        }
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  }

  function reset(): void {
    setValid(false);
    setError(false);
    setLoading(false);
    setItem(null);
    if (props.open) form.resetFields();
  }

  function done(): void {
    setLoading(false);
    props.onFinish?.();
  }

  function fail(e: any): void {
    setErrorMessage(getProblemMessage(e));
    setLoading(false);
    setError(true);
  }

  function onSubmit(): void {
    setLoading(true);
    setError(false);

    if (props.mode === "delete") {
      deleteCompanyIncidentNotifier(props.uid!).then(done).catch(fail);
      return;
    }

    const { incidentType, companyRole } = values;
    const payload = {
      incidentTypeUid: (incidentType as any)?.value,
      companyRoleUid: (companyRole as any)?.value,
      companyUid: props.companyUid,
    };

    if (props.mode === "add") {
      addCompanyIncidentNotifier(payload as any).then(done).catch(fail);
    } else {
      updateCompanyIncidentNotifier({
        uid: item?.uid,
        rowVersion: item?.rowVersion,
        ...payload,
      } as any)
        .then(done)
        .catch(fail);
    }
  }

  const title =
    (props.mode === "add"
      ? "Add"
      : props.mode === "update"
        ? "Edit"
        : "Delete") + " Incident Notifier";

  return (
    <InModal
      open={props.open}
      title={title}
      size="small"
      onCancel={() => props.onClose?.()}
      actions={
        <Space className="sticky bottom-0 flex flex-row items-center justify-end">
          <Button disabled={loading} onClick={() => props.onClose?.()}>
            Cancel
          </Button>
          <Button
            type="primary"
            danger={props.mode === "delete"}
            loading={loading}
            disabled={props.mode !== "delete" && !valid}
            onClick={onSubmit}
          >
            {props.mode === "add"
              ? "Add"
              : props.mode === "update"
                ? "Save"
                : "Delete"}
          </Button>
        </Space>
      }
    >
      <Col className="flex flex-col">
        {props.mode !== "delete" && (
          <DynamicForm
            className="w-auto"
            form={form}
            schema={{
              groups: [
                {
                  controls: [
                    {
                      type: "combobox",
                      name: "incidentType",
                      label: "Incident Type",
                      request: incidentTypesRequest,
                      rules: [
                        { required: true, message: "Incident type is required" },
                      ],
                    },
                    {
                      type: "combobox",
                      name: "companyRole",
                      label: "Company Role",
                      // Scoped to this company (the role list does not depend on the incident type, so no cascade —
                      // a dependsOn here would only wipe the selected role when the type changes).
                      request: companyRolesRequest,
                      rules: [
                        { required: true, message: "Company role is required" },
                      ],
                    },
                  ],
                },
              ],
            }}
          />
        )}
        {props.mode === "delete" && (
          <Typography.Paragraph>
            <Typography.Text>
              Continue deleting the notifier for{" "}
              <Typography.Text className="font-bold">
                {item?.companyRoleName}
              </Typography.Text>{" "}
              on{" "}
              <Typography.Text className="font-bold">
                {item?.incidentTypeName}
              </Typography.Text>
              ?
            </Typography.Text>
          </Typography.Paragraph>
        )}
        {error && <Alert className="mb-2" message={errorMessage} type="error" />}
      </Col>
    </InModal>
  );
}

export default IncidentNotifierView;
